import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, Globe, Search, MessageSquare, CreditCard, Mail, Image as ImageIcon, HardDrive, Upload, CheckCircle2, Smartphone, GitBranch, Hammer, ExternalLink, RefreshCw, Database, Trash2, Sparkles, ToggleLeft, Activity, Ticket, UtensilsCrossed, Car, Code2, FileText } from 'lucide-react';
import KeystoreSection from './KeystoreSection';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
    <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1" />
  </div>
);

const SwitchRow = ({ label, value, onChange, hint }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
    <div>
      <div className="text-sm font-semibold text-[#0B2E5C]">{label}</div>
      {hint && <div className="text-xs text-gray-500">{hint}</div>}
    </div>
    <Switch checked={!!value} onCheckedChange={onChange} />
  </div>
);

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [builds, setBuilds] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [building, setBuilding] = useState('');
  const [testingCab, setTestingCab] = useState(false);
  const [cabTest, setCabTest] = useState(null);

  const token = () => localStorage.getItem('tp_admin_token');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token()}` } });
        setSettings(r.data);
      } catch (e) {
        toast({ title: 'Failed to load settings' });
      }
    };
    load();
  }, []);

  const mob = (settings && settings.mobile) || {};

  const loadBuilds = async () => {
    try {
      const r = await axios.get(`${API}/admin/mobile/builds`, { headers: { Authorization: `Bearer ${token()}` } });
      setBuilds(r.data.runs || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (mob.github_owner && mob.github_repo && mob.github_token) loadBuilds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mob.github_owner, mob.github_repo, mob.github_token]);

  const update = (section, key, value) => {
    setSettings(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const testCabs = async () => {
    setTestingCab(true); setCabTest(null);
    try {
      await axios.put(`${API}/admin/settings`, settings, { headers: { Authorization: `Bearer ${token()}` } });
      const r = await axios.post(`${API}/admin/cabs/test`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      setCabTest(r.data);
    } catch (e) {
      setCabTest({ ok: false, status: 'error', message: e.response?.data?.detail || 'Request failed' });
    } finally { setTestingCab(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings`, settings, { headers: { Authorization: `Bearer ${token()}` } });
      setSettings(r.data);
      toast({ title: 'Settings saved', description: 'Changes applied successfully' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const pushToRepo = async () => {
    if (!mob.github_owner || !mob.github_repo || !mob.github_token) { toast({ title: 'Configure GitHub fields first' }); return; }
    setPushing(true);
    try {
      const r = await axios.post(`${API}/admin/mobile/push`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      toast({ title: 'Pushed!', description: `Commit ${r.data.commit} on ${r.data.branch}` });
      loadBuilds();
    } catch (err) {
      toast({ title: 'Push failed', description: err.response?.data?.detail?.slice(0, 200) || 'Try again' });
    } finally { setPushing(false); }
  };

  const pushAndBuild = async (kind) => {
    if (!mob.github_owner || !mob.github_repo || !mob.github_token) { toast({ title: 'Configure GitHub fields first' }); return; }
    setBuilding(kind);
    try {
      // Save current settings first so build_number, version, etc. are persisted
      await axios.put(`${API}/admin/settings`, settings, { headers: { Authorization: `Bearer ${token()}` } });
      const r = await axios.post(`${API}/admin/mobile/push-and-build?workflow=${kind}`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      const cm = r.data?.push?.commit;
      toast({ title: `${kind.toUpperCase()} build started`, description: `Pushed ${cm}, GitHub Actions running. Check the Recent Builds list below.` });
      // Re-fetch settings (build_number was incremented server-side)
      const s = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token()}` } });
      setSettings(s.data);
      setTimeout(loadBuilds, 3500);
    } catch (err) {
      toast({ title: 'Build failed', description: err.response?.data?.detail?.slice(0, 200) || 'Try again' });
    } finally {
      setBuilding('');
    }
  };

  const triggerBuild = async (kind) => {
    setBuilding(kind);
    try {
      await axios.post(`${API}/admin/mobile/build?workflow=${kind}`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      toast({ title: `${kind.toUpperCase()} build triggered`, description: 'Check GitHub Actions for progress' });
      setTimeout(loadBuilds, 2500);
    } catch (err) {
      toast({ title: 'Build trigger failed', description: err.response?.data?.detail?.slice(0, 200) || 'Try again' });
    } finally { setBuilding(''); }
  };

  if (!settings) {
    return <div className="text-center py-20 text-gray-500"><Loader2 className="animate-spin mx-auto" /></div>;
  }

  const g = settings.general || {};
  const s = settings.seo || {};
  const sm = settings.sms || {};
  const p = settings.payment || {};
  const e = settings.email || {};
  const st = settings.storage || {};
  const cab = settings.myf_cabs || {};
  const ci = settings.code_injection || {};
  const fn = settings.feature_notes || {};

  const uploadFile = async (file, folder, onUrl) => {
    if (!st.enabled) {
      toast({ title: 'Storage not enabled', description: 'Save Bunny settings and toggle Enable Storage first.' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/admin/upload?folder=${encodeURIComponent(folder)}`, fd, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Uploaded', description: r.data.url });
      onUrl(r.data.url);
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const UploadButton = ({ folder, onUrl, label = 'Upload' }) => {
    const inputId = `upl-${folder}-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <label htmlFor={inputId} className="inline-flex items-center gap-1.5 h-9 px-3 bg-white border-2 border-tp-orange text-tp-orange rounded-lg text-xs font-semibold cursor-pointer hover:bg-[#FFF3E6]">
        <Upload size={13} /> {label}
        <input id={inputId} type="file" accept="image/*" className="hidden" onChange={ev => {
          const f = ev.target.files?.[0];
          if (f) uploadFile(f, folder, onUrl);
          ev.target.value = '';
        }} />
      </label>
    );
  };

  const testBunny = async () => {
    try {
      const r = await axios.post(`${API}/admin/storage/test`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      if (r.data.ok) toast({ title: 'Connected', description: r.data.message });
      else toast({ title: 'Failed', description: r.data.message || 'Not connected' });
    } catch (err) {
      toast({ title: 'Test failed', description: err.response?.data?.detail || 'Error' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0B2E5C]">Settings</h1>
          <p className="text-sm text-gray-500">Configure site branding, integrations and preferences</p>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-tp-orange hover:bg-[#E07A0F] text-white font-semibold">
          {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex flex-wrap w-full mb-5 h-auto bg-white border border-gray-100 p-1 gap-1">
          <TabsTrigger value="general" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Globe size={14} /> General</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><ToggleLeft size={14} /> Modules</TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Search size={14} /> SEO</TabsTrigger>
          <TabsTrigger value="code" data-testid="tab-code" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Code2 size={14} /> Code</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><FileText size={14} /> Notes</TabsTrigger>
          <TabsTrigger value="storage" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><HardDrive size={14} /> Storage</TabsTrigger>
          <TabsTrigger value="mobile" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Smartphone size={14} /> Mobile App</TabsTrigger>
          <TabsTrigger value="cabs" data-testid="tab-cabs" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Car size={14} /> Cabs</TabsTrigger>
          <TabsTrigger value="stories" data-testid="tab-stories" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Sparkles size={14} /> AI Stories</TabsTrigger>
          <TabsTrigger value="scraper" data-testid="tab-scraper" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Database size={14} /> Scraper</TabsTrigger>
          <TabsTrigger value="sms" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><MessageSquare size={14} /> SMS</TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><CreditCard size={14} /> Payment</TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-1.5 data-[state=active]:bg-[#0B2E5C] data-[state=active]:text-white py-2"><Mail size={14} /> Email</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Globe size={16} className="text-tp-orange" /> Branding & Contact</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Site Name" value={g.site_name} onChange={v => update('general', 'site_name', v)} placeholder="TrainPanda" />
              <Field label="Tagline" value={g.tagline} onChange={v => update('general', 'tagline', v)} placeholder="Your trusted companion" />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Logo URL</label>
                  <UploadButton folder="logo" onUrl={url => update('general', 'logo_url', url)} label="Upload logo" />
                </div>
                <Input value={g.logo_url || ''} onChange={ev => update('general', 'logo_url', ev.target.value)} placeholder="https://..." />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Favicon URL</label>
                  <UploadButton folder="favicon" onUrl={url => update('general', 'favicon_url', url)} label="Upload favicon" />
                </div>
                <Input value={g.favicon_url || ''} onChange={ev => update('general', 'favicon_url', ev.target.value)} placeholder="https://..." />
              </div>
              <Field label="Support Email" value={g.support_email} onChange={v => update('general', 'support_email', v)} placeholder="help@..." />
              <Field label="Support Phone" value={g.support_phone} onChange={v => update('general', 'support_phone', v)} placeholder="1800-..." />
            </div>
            {g.logo_url && (
              <div className="border-t pt-4">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1"><ImageIcon size={12} /> Logo Preview</div>
                <div className="bg-tp-navy p-4 rounded-lg inline-block">
                  <img src={g.logo_url} alt="preview" className="h-12 w-auto" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Search size={16} className="text-tp-orange" /> SEO & Metadata</h3>
            <Field label="Meta Title" value={s.meta_title} onChange={v => update('seo', 'meta_title', v)} placeholder="Page title" />
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Meta Description</label>
              <Textarea value={s.meta_description || ''} onChange={ev => update('seo', 'meta_description', ev.target.value)} rows={3} className="mt-1" />
            </div>
            <Field label="Meta Keywords (comma separated)" value={s.meta_keywords} onChange={v => update('seo', 'meta_keywords', v)} placeholder="train, pnr, food" />
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Open Graph Image URL</label>
                  <UploadButton folder="og" onUrl={url => update('seo', 'og_image', url)} label="Upload OG image" />
                </div>
                <Input value={s.og_image || ''} onChange={ev => update('seo', 'og_image', ev.target.value)} placeholder="https://..." />
              </div>
              <Field label="Google Analytics ID" value={s.google_analytics_id} onChange={v => update('seo', 'google_analytics_id', v)} placeholder="G-XXXXXXXXXX" />
            </div>
          </div>
        </TabsContent>

        {/* CODE / ANALYTICS INJECTION */}
        <TabsContent value="code">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-5">
            <div>
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Code2 size={16} className="text-tp-orange" /> Custom Code / Analytics</h3>
              <p className="text-xs text-gray-500 mt-1">Paste any JavaScript / HTML snippets (Google Analytics, Google Tag Manager, Meta Pixel, Hotjar, chat widgets, etc.). They are injected on <b>every page</b> of the site. Include the full <code>&lt;script&gt;...&lt;/script&gt;</code> tags.</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Head Code — injected into &lt;head&gt;</label>
              <p className="text-[11px] text-gray-400 mb-1">Best for Google Analytics (gtag.js), GTM head snippet, verification meta tags, and preconnect links.</p>
              <Textarea
                data-testid="code-head-input"
                value={ci.head_code || ''}
                onChange={ev => update('code_injection', 'head_code', ev.target.value)}
                rows={9}
                spellCheck={false}
                className="mt-1 font-mono text-xs"
                placeholder={'<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag(\'js\', new Date());\n  gtag(\'config\', \'G-XXXXXXXXXX\');\n</script>'}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Body Code — injected before &lt;/body&gt;</label>
              <p className="text-[11px] text-gray-400 mb-1">Best for GTM &lt;noscript&gt; snippet, chat widgets, or scripts that should load last.</p>
              <Textarea
                data-testid="code-body-input"
                value={ci.body_code || ''}
                onChange={ev => update('code_injection', 'body_code', ev.target.value)}
                rows={7}
                spellCheck={false}
                className="mt-1 font-mono text-xs"
                placeholder={'<!-- e.g. Tag Manager (noscript), chat widget, etc. -->'}
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
              <div><b>Notes:</b></div>
              <div>• Click <b>Save Changes</b>, then hard-refresh (Ctrl/Cmd+Shift+R) to see the code load.</div>
              <div>• Only paste code from sources you trust — it runs on every visitor&apos;s browser.</div>
              <div>• For Google Search Console you can either use the verification <code>.html</code> file already at the site root, or paste the verification <code>&lt;meta&gt;</code> tag into Head Code above.</div>
            </div>
          </div>
        </TabsContent>

        {/* FEATURE NOTES / TERMS */}
        <TabsContent value="notes">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-5">
            <div>
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><FileText size={16} className="text-tp-orange" /> Feature Notes / Terms &amp; Conditions</h3>
              <p className="text-xs text-gray-500 mt-1">Add a short note or T&amp;C shown <b>below the main content</b> of each feature page. Leave a field empty to hide it. Notes sync automatically to both Web and the Mobile app.</p>
            </div>
            {[
              { key: 'pnr_status', label: 'PNR Status', icon: Ticket },
              { key: 'live_status', label: 'Live Train Status', icon: Activity },
              { key: 'schedule', label: 'Train Tools / Schedule', icon: Search },
              { key: 'cabs', label: 'Cab Booking', icon: Car },
              { key: 'food_on_train', label: 'Food on Train', icon: UtensilsCrossed },
              { key: 'stories', label: 'Travel Stories', icon: Sparkles },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5"><f.icon size={13} className="text-tp-orange" /> {f.label}</label>
                <Textarea
                  data-testid={`note-${f.key}`}
                  value={fn[f.key] || ''}
                  onChange={ev => update('feature_notes', f.key, ev.target.value)}
                  rows={3}
                  className="mt-1 text-sm"
                  placeholder={`e.g. Fares & timings are indicative. Please reconfirm before travel. (leave blank to hide)`}
                />
              </div>
            ))}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              Save, then the note appears instantly on the matching page (web + app). Line breaks are preserved.
            </div>
          </div>
        </TabsContent>

        {/* MODULES (feature flags) */}
        <TabsContent value="modules">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><ToggleLeft size={16} className="text-tp-orange" /> Module Visibility</h3>
            <p className="text-xs text-gray-500 -mt-2">Turn modules on/off. Disabled modules are hidden everywhere — navbar, home cards, direct routes, mobile tabs.</p>

            {[
              { key: 'live_status',   icon: Activity,        label: 'Live Train Status', desc: 'Real-time train tracking page (/live-train-status)', accent: 'bg-tp-violet-soft text-tp-violet-deep' },
              { key: 'pnr_status',    icon: Ticket,          label: 'PNR Status',        desc: 'PNR lookup with coach/berth (/pnr-status)',           accent: 'bg-tp-coral-soft text-tp-coral-deep' },
              { key: 'food_on_train', icon: UtensilsCrossed, label: 'Food on Train',     desc: 'Vendor-embed ordering (/food-on-train)',              accent: 'bg-tp-lime-soft text-tp-lime-deep' },
              { key: 'cab_booking',   icon: Car,             label: 'Cab Booking',       desc: 'Mind Your Fleet cab booking (/cabs)',                 accent: 'bg-tp-sky-soft text-tp-sky-deep' },
              { key: 'stories',       icon: Sparkles,        label: 'Travel Stories',    desc: 'AI-generated articles (/stories)',                    accent: 'bg-tp-pink-soft text-tp-pink-deep' },
            ].map(m => {
              const on = !!(settings.modules || {})[m.key];
              return (
                <div key={m.key} className="flex items-center gap-3 p-3 border rounded-2xl hover:border-tp-violet transition-colors">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.accent}`}>
                    <m.icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-tp-ink text-sm">{m.label}</div>
                    <div className="text-[11px] text-gray-500">{m.desc}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${on ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {on ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      data-testid={`module-toggle-${m.key}`}
                      checked={on}
                      onCheckedChange={v => update('modules', m.key, v)}
                    />
                  </div>
                </div>
              );
            })}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <b>Note:</b> Changes take effect immediately after saving. Public users may need to hard-refresh (Ctrl+Shift+R) to see the change.
            </div>
          </div>
        </TabsContent>

        {/* STORAGE */}
        <TabsContent value="storage">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><HardDrive size={16} className="text-tp-orange" /> Bunny.net Storage</h3>
              <Button onClick={testBunny} variant="outline" className="border-tp-orange text-tp-orange hover:bg-[#FFF3E6] h-9"><CheckCircle2 size={14} className="mr-1.5" /> Test Connection</Button>
            </div>
            <SwitchRow label="Enable Bunny Storage Uploads" hint="When enabled, logo/favicon/OG image uploads are stored on Bunny CDN" value={st.enabled} onChange={v => update('storage', 'enabled', v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Storage Zone Name" value={st.bunny_storage_zone} onChange={v => update('storage', 'bunny_storage_zone', v)} placeholder="trainpanda-assets" />
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Region</label>
                <Select value={st.bunny_region || ''} onValueChange={v => update('storage', 'bunny_region', v === 'de' ? '' : v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Default (Falkenstein, DE)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Default (Falkenstein, DE)</SelectItem>
                    <SelectItem value="ny">New York (NY)</SelectItem>
                    <SelectItem value="la">Los Angeles (LA)</SelectItem>
                    <SelectItem value="sg">Singapore (SG)</SelectItem>
                    <SelectItem value="syd">Sydney (SYD)</SelectItem>
                    <SelectItem value="uk">London (UK)</SelectItem>
                    <SelectItem value="br">São Paulo (BR)</SelectItem>
                    <SelectItem value="jh">Johannesburg (JH)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field type="password" label="Access Key (Storage Password)" value={st.bunny_access_key} onChange={v => update('storage', 'bunny_access_key', v)} placeholder="******" />
              <Field label="Pull Zone URL" value={st.bunny_pull_zone_url} onChange={v => update('storage', 'bunny_pull_zone_url', v)} placeholder="https://trainpanda.b-cdn.net" />
              <Field label="Base Folder Path" value={st.bunny_base_path} onChange={v => update('storage', 'bunny_base_path', v)} placeholder="uploads" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
              <div><b>How to get these:</b></div>
              <div>1. Log in to Bunny.net dashboard</div>
              <div>2. Storage → create or open a Storage Zone → copy its <b>Name</b> and <b>Password</b> (that&apos;s the Access Key)</div>
              <div>3. Attach the zone to a Pull Zone (or create one) and copy its CDN URL (e.g. <code>https://trainpanda.b-cdn.net</code>)</div>
              <div>4. Enable and click <b>Test Connection</b> above to verify</div>
            </div>
          </div>
        </TabsContent>

        {/* MOBILE APP */}
        <TabsContent value="mobile">
          <div className="space-y-5">
            {/* IDENTITY */}
            <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Smartphone size={16} className="text-tp-orange" /> 1. App Identity</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="App Name" value={mob.app_name} onChange={v => update('mobile', 'app_name', v)} placeholder="TrainPanda" />
                <Field label="Package / Bundle ID" value={mob.bundle_id} onChange={v => update('mobile', 'bundle_id', v)} placeholder="com.trainpanda.app" />
                <Field label="Version (semver)" value={mob.version} onChange={v => update('mobile', 'version', v)} placeholder="1.0.0" data-testid="mobile-version" />
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Build Number (versionCode)</label>
                  <Input type="number" min={1} value={mob.build_number ?? 1} onChange={ev => update('mobile', 'build_number', parseInt(ev.target.value) || 1)} data-testid="mobile-build-number" />
                </div>
                <Field label="API Base URL" value={mob.api_base_url} onChange={v => update('mobile', 'api_base_url', v)} placeholder="https://www.trainpanda.com" />
                <div className="flex items-center gap-3 pt-5">
                  <Switch checked={mob.auto_increment_build !== false} onCheckedChange={v => update('mobile', 'auto_increment_build', v)} data-testid="mobile-auto-increment" />
                  <div>
                    <div className="text-sm font-semibold">Auto-increment build number</div>
                    <div className="text-xs text-gray-500">Each push-and-build bumps versionCode by 1 (required by Play Store)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* BRANDING */}
            <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><ImageIcon size={16} className="text-tp-orange" /> 2. Branding</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase">App Logo URL</label>
                    <UploadButton folder="app/logo" onUrl={url => update('mobile', 'app_logo_url', url)} label="Upload" />
                  </div>
                  <Input value={mob.app_logo_url || ''} onChange={ev => update('mobile', 'app_logo_url', ev.target.value)} placeholder="https://..." />
                  {mob.app_logo_url && <img src={mob.app_logo_url} alt="logo" className="h-10 mt-2" />}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase">App Icon URL (1024×1024)</label>
                    <UploadButton folder="app/icon" onUrl={url => update('mobile', 'app_icon_url', url)} label="Upload" />
                  </div>
                  <Input value={mob.app_icon_url || ''} onChange={ev => update('mobile', 'app_icon_url', ev.target.value)} placeholder="https://..." />
                  {mob.app_icon_url && <img src={mob.app_icon_url} alt="icon" className="h-10 w-10 mt-2 rounded-lg" />}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Splash Image URL</label>
                    <UploadButton folder="app/splash" onUrl={url => update('mobile', 'splash_url', url)} label="Upload" />
                  </div>
                  <Input value={mob.splash_url || ''} onChange={ev => update('mobile', 'splash_url', ev.target.value)} placeholder="https://..." />
                  {mob.splash_url && <img src={mob.splash_url} alt="splash" className="h-10 mt-2" />}
                </div>
                <Field label="Splash BG Color (hex)" value={mob.splash_bg_color} onChange={v => update('mobile', 'splash_bg_color', v)} placeholder="#0B2E5C" />
                <Field label="Primary Color (hex)" value={mob.primary_color} onChange={v => update('mobile', 'primary_color', v)} placeholder="#F68B1F" />
              </div>
            </div>

            {/* GITHUB CONNECTION */}
            <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><GitBranch size={16} className="text-tp-orange" /> 3. GitHub Repo</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="GitHub Owner / Username" value={mob.github_owner} onChange={v => update('mobile', 'github_owner', v)} placeholder="techeela" />
                <Field label="Repo Name" value={mob.github_repo} onChange={v => update('mobile', 'github_repo', v)} placeholder="trainpandamobileapp" />
                <Field type="password" label="Personal Access Token (repo + workflow)" value={mob.github_token} onChange={v => update('mobile', 'github_token', v)} placeholder="ghp_..." />
                <Field label="Branch" value={mob.github_branch} onChange={v => update('mobile', 'github_branch', v)} placeholder="main" />
              </div>
              {mob.last_pushed_at && <div className="text-xs text-gray-500">Last push: {new Date(mob.last_pushed_at).toLocaleString()} {mob.last_pushed_commit && `(${mob.last_pushed_commit})`}</div>}
            </div>

            {/* KEYSTORE (Play-Store signing) */}
            <KeystoreSection token={token} />

            {/* GENERATE BUILD */}
            <div className="bg-white rounded-xl border-2 border-tp-orange/40 card-shadow p-6 space-y-4 bg-gradient-to-br from-white to-[#FFF8F1]">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Hammer size={16} className="text-tp-orange" /> 5. Generate Build</h3>
              <p className="text-sm text-gray-600">
                One click pushes the latest mobile-app code (with version <span className="font-mono font-semibold">v{mob.version || '1.0.0'}+{(mob.build_number || 1)}</span>) to <span className="font-mono">{mob.github_owner}/{mob.github_repo}</span> and starts a GitHub Actions build. The signed APK/AAB will be available as a workflow artifact when complete.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button data-testid="generate-apk-btn" onClick={() => pushAndBuild('apk')} disabled={building === 'apk' || !mob.github_token} className="bg-tp-orange hover:bg-[#E07A0F] text-white px-6">
                  {building === 'apk' ? <Loader2 className="animate-spin mr-2" size={14} /> : <Hammer size={14} className="mr-2" />}
                  Generate APK (Debug)
                </Button>
                <Button data-testid="generate-aab-btn" onClick={() => pushAndBuild('aab')} disabled={building === 'aab' || !mob.github_token} className="bg-tp-navy hover:bg-[#082448] text-white px-6">
                  {building === 'aab' ? <Loader2 className="animate-spin mr-2" size={14} /> : <Hammer size={14} className="mr-2" />}
                  Generate AAB (Release)
                </Button>
              </div>
              <div className="border-t pt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 uppercase font-semibold mr-2">Advanced</span>
                <Button onClick={pushToRepo} disabled={pushing} variant="outline" size="sm">
                  {pushing ? <Loader2 className="animate-spin mr-1.5" size={12} /> : <Upload size={12} className="mr-1.5" />}
                  Push only (no build)
                </Button>
                <Button onClick={() => triggerBuild('apk')} disabled={building === 'apk' || !mob.github_token} variant="outline" size="sm"><Hammer size={12} className="mr-1.5" /> Build APK only</Button>
                <Button onClick={() => triggerBuild('aab')} disabled={building === 'aab' || !mob.github_token} variant="outline" size="sm"><Hammer size={12} className="mr-1.5" /> Build AAB only</Button>
              </div>
            </div>

            {/* RECENT BUILDS */}
            <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[#0B2E5C]">Recent Builds</h3>
                <div className="flex items-center gap-3">
                  <Button onClick={loadBuilds} variant="ghost" size="sm"><RefreshCw size={14} /></Button>
                  {mob.github_owner && mob.github_repo && (
                    <a href={`https://github.com/${mob.github_owner}/${(mob.github_repo || '').replace('.git', '')}/actions`} target="_blank" rel="noreferrer" className="text-xs text-tp-orange hover:underline flex items-center gap-1"><ExternalLink size={12} /> Open GitHub Actions</a>
                  )}
                </div>
              </div>
              {builds.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">No builds yet — fill in the fields above and click <span className="font-semibold text-tp-orange">Generate APK</span>.</div>
              ) : (
                <div className="space-y-2">
                  {builds.map(b => (
                    <a key={b.id} href={b.html_url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 border rounded-lg hover:border-tp-orange">
                      <div>
                        <div className="font-semibold text-[#0B2E5C] text-sm">{b.name}</div>
                        <div className="text-xs text-gray-500">{b.head_branch} • {b.head_sha} • {new Date(b.created_at).toLocaleString()}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${b.status === 'completed' && b.conclusion === 'success' ? 'bg-green-100 text-green-700' : b.status === 'in_progress' || b.status === 'queued' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {b.status === 'completed' ? b.conclusion : b.status}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* AI STORIES */}
        <TabsContent value="stories">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-5">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Sparkles size={16} className="text-tp-orange" /> AI Travel Stories Generator</h3>
            <p className="text-xs text-gray-500 -mt-3">Auto-write 900+ word travel articles with 1 hero + 2 inline images, hosted on your Bunny CDN. Uses your <b>own</b> OpenAI key (not Emergent).</p>

            <div className="grid md:grid-cols-2 gap-4">
              <Field
                type="password"
                label="OpenAI API Key"
                value={(settings.openai || {}).api_key}
                onChange={v => update('openai', 'api_key', v)}
                placeholder="sk-proj-…"
              />
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Text Model</label>
                <Select value={(settings.openai || {}).text_model || 'gpt-4o-mini'} onValueChange={v => update('openai', 'text_model', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">gpt-4o-mini (cheap, fast)</SelectItem>
                    <SelectItem value="gpt-4o">gpt-4o (premium)</SelectItem>
                    <SelectItem value="gpt-4.1-mini">gpt-4.1-mini</SelectItem>
                    <SelectItem value="gpt-4.1">gpt-4.1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Image Model</label>
                <Select value={(settings.openai || {}).image_model || 'gpt-image-1'} onValueChange={v => update('openai', 'image_model', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-image-1">gpt-image-1 (recommended)</SelectItem>
                    <SelectItem value="dall-e-3">dall-e-3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <SwitchRow
              label="Daily auto-generate at 08:00 IST"
              hint="When ON, the server generates one new travel story every morning. Requires OpenAI key + Bunny Storage configured."
              value={(settings.openai || {}).auto_daily_enabled}
              onChange={v => update('openai', 'auto_daily_enabled', v)}
            />

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
              <div><b>Setup checklist:</b></div>
              <div>1. Paste your OpenAI key above &amp; click <b>Save Changes</b></div>
              <div>2. Make sure <b>Storage → Bunny.net</b> is configured + Enabled (images go there)</div>
              <div>3. Open <a href="/admin/stories" className="text-tp-orange font-semibold underline">Stories</a> to generate, edit, or delete</div>
              <div>4. Toggle &quot;Daily auto-generate&quot; ON for hands-free posting</div>
            </div>

            {((settings.openai || {}).last_cron_run_at) && (
              <div className="border-t pt-3 text-xs text-gray-600 space-y-1">
                <div><b>Last cron run:</b> {new Date(settings.openai.last_cron_run_at).toLocaleString()}</div>
                <div><b>Status:</b> {settings.openai.last_cron_status || '—'}</div>
                {settings.openai.last_cron_story_id && <div><b>Last story id:</b> {settings.openai.last_cron_story_id}</div>}
              </div>
            )}
          </div>
        </TabsContent>

        {/* SCRAPER */}
        <TabsContent value="scraper">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Database size={16} className="text-tp-orange" /> Scraper Configuration</h3>
              <Button
                data-testid="clear-cache-btn"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/cache/clear`, {}, { headers: { Authorization: `Bearer ${token()}` } });
                    toast({ title: 'Cache cleared', description: 'Schedule + PNR + live caches purged' });
                  } catch (err) {
                    toast({ title: 'Failed', description: err.response?.data?.detail || 'Try again' });
                  }
                }}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} className="mr-1.5" /> Clear All Caches Now
              </Button>
            </div>

            <SwitchRow
              label="In-Memory Caching"
              hint="When ON, schedule data is cached for 24h, PNR for 60s, live status for 90s. Turn OFF to always fetch fresh data from upstream sources (slower, but always real-time)."
              value={(settings.scraper || {}).cache_enabled !== false}
              onChange={v => update('scraper', 'cache_enabled', v)}
            />

            <div className="border-t pt-4">
              <div className="text-sm font-semibold text-[#0B2E5C] mb-2">Active Scrapers</div>
              <p className="text-xs text-gray-500 mb-3">Choose which upstream sites should be tried, in priority order. Disabled scrapers are skipped entirely.</p>

              {[
                { id: 'railyatri', label: 'RailYatri.in', site: 'railyatri.in' },
                { id: 'confirmtkt', label: 'ConfirmTkt.com', site: 'confirmtkt.com' },
                { id: 'erail', label: 'eRail.in', site: 'erail.in' },
              ].map(sc => {
                const enabled = ((settings.scraper || {}).enabled_scrapers || []).includes(sc.id);
                return (
                  <div key={sc.id} className="flex items-center justify-between py-2.5 border-b last:border-b-0">
                    <div>
                      <div className="text-sm font-semibold">{sc.label}</div>
                      <div className="text-xs text-gray-500">{sc.site}</div>
                    </div>
                    <Switch
                      data-testid={`scraper-toggle-${sc.id}`}
                      checked={enabled}
                      onCheckedChange={v => {
                        const cur = (settings.scraper || {}).enabled_scrapers || [];
                        const next = v ? Array.from(new Set([...cur, sc.id])) : cur.filter(x => x !== sc.id);
                        // preserve canonical priority order
                        const order = ['railyatri', 'confirmtkt', 'erail'];
                        update('scraper', 'enabled_scrapers', order.filter(x => next.includes(x)));
                      }}
                    />
                  </div>
                );
              })}

              {!((settings.scraper || {}).enabled_scrapers || []).length && (
                <div className="mt-3 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-700 p-2">
                  Warning: All scrapers are disabled. The app will return placeholder data only.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* SMS */}
        <TabsContent value="sms">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><MessageSquare size={16} className="text-tp-orange" /> SMS Gateway (for OTP)</h3>
            <SwitchRow label="Enable SMS OTP" hint="Dispatch real OTP via configured SMS provider" value={sm.enabled} onChange={v => update('sms', 'enabled', v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Provider</label>
                <Select value={sm.provider || 'fast2sms'} onValueChange={v => update('sms', 'provider', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fast2sms">Fast2SMS (Recommended - Easy OTP)</SelectItem>
                    <SelectItem value="msg91">MSG91</SelectItem>
                    <SelectItem value="textlocal">TextLocal</SelectItem>
                    <SelectItem value="twilio">Twilio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field
                label={sm.provider === 'fast2sms' ? 'Sender ID (Optional)' : 'Sender ID / Header'}
                value={sm.sender_id}
                onChange={v => update('sms', 'sender_id', v)}
                placeholder={sm.provider === 'fast2sms' ? 'FSTSMS / TRNPDA' : sm.provider === 'twilio' ? '+1234567890' : 'TRNPDA'}
              />
              <Field
                label={
                  sm.provider === 'fast2sms' ? 'Fast2SMS API Key' :
                  sm.provider === 'twilio' ? 'Twilio Account SID (AC...)' :
                  sm.provider === 'msg91' ? 'MSG91 Auth Key' :
                  'API Key'
                }
                value={sm.api_key}
                onChange={v => update('sms', 'api_key', v)}
                placeholder={
                  sm.provider === 'fast2sms' ? 'Paste Fast2SMS Dev API Key' :
                  sm.provider === 'twilio' ? 'ACXXXXXXXXXXXXXXXXXXXXXXX' :
                  'Enter API Key'
                }
              />
              {sm.provider !== 'fast2sms' && sm.provider !== 'textlocal' ? (
                <Field
                  type="password"
                  label={
                    sm.provider === 'twilio' ? 'Twilio Auth Token' :
                    sm.provider === 'msg91' ? 'DLT Template ID (OTP)' :
                    'API Secret / Token'
                  }
                  value={sm.api_secret}
                  onChange={v => update('sms', 'api_secret', v)}
                  placeholder={sm.provider === 'msg91' ? 'e.g. 6423fa8c9...' : '••••••'}
                />
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 flex items-center">
                  <p className="text-xs text-gray-500">
                    <span className="font-bold text-green-600">✓ No Secret/Template required</span> for Fast2SMS OTP route. Just enter your API Key.
                  </p>
                </div>
              )}
            </div>
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 space-y-1">
              <p className="font-semibold">ℹ️ Provider Guidance:</p>
              <p>• <b>Fast2SMS</b>: Login to fast2sms.com ➔ Dev API ➔ Copy Authorization Key ➔ Paste in <b>Fast2SMS API Key</b>.</p>
              <p>• <b>Twilio</b>: Paste Account SID (starts with AC...) and Auth Token.</p>
              <p>• <b>MSG91</b>: Paste Auth Key in API Key, and DLT Template ID in Template ID.</p>
            </div>
          </div>
        </TabsContent>

        {/* PAYMENT */}
        <TabsContent value="payment">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><CreditCard size={16} className="text-tp-orange" /> Razorpay Payment Gateway</h3>
            <SwitchRow label="Enable Razorpay Online Payment" hint="Allow customers to pay via UPI, Cards, Netbanking" value={p.enabled} onChange={v => update('payment', 'enabled', v)} />
            <SwitchRow label="Enable Cash on Delivery" hint="Allow customers to pay when food is delivered" value={p.cod_enabled} onChange={v => update('payment', 'cod_enabled', v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Mode</label>
                <Select value={p.mode || 'test'} onValueChange={v => update('payment', 'mode', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test Mode</SelectItem>
                    <SelectItem value="live">Live Mode</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Razorpay Key ID" value={p.razorpay_key_id} onChange={v => update('payment', 'razorpay_key_id', v)} placeholder="rzp_test_..." />
              <Field type="password" label="Razorpay Key Secret" value={p.razorpay_key_secret} onChange={v => update('payment', 'razorpay_key_secret', v)} placeholder="••••••" />
              <Field type="password" label="Webhook Secret" value={p.razorpay_webhook_secret} onChange={v => update('payment', 'razorpay_webhook_secret', v)} placeholder="••••••" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              Get keys from your Razorpay Dashboard → Settings → API Keys. Use Test mode keys during development.
            </div>
          </div>
        </TabsContent>

        {/* CABS (Mind Your Fleet) */}
        <TabsContent value="cabs">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Car size={16} className="text-tp-orange" /> Mind Your Fleet (Cab Booking)</h3>
            <SwitchRow label="Enable Cab Booking module" hint="Single switch — also reflected on the Modules tab. Shows Cabs on web & mobile and activates the API." value={(settings.modules || {}).cab_booking} onChange={v => update('modules', 'cab_booking', v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="MYF API Token" value={cab.token} onChange={v => update('myf_cabs', 'token', v)} placeholder="MYF marketplace token" />
              <Field type="password" label="Security Salt" value={cab.salt} onChange={v => update('myf_cabs', 'salt', v)} placeholder="rqid checksum salt" />
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Environment</label>
                <Select value={cab.environment || 'production'} onValueChange={v => update('myf_cabs', 'environment', v)}>
                  <SelectTrigger className="mt-1" data-testid="cab-env-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Corporate ID" value={cab.corporate_id} onChange={v => update('myf_cabs', 'corporate_id', v)} placeholder="e.g. 1 (if required by MYF account)" />
              <Field label="Category IDs (comma separated)" value={cab.category_ids} onChange={v => update('myf_cabs', 'category_ids', v)} placeholder="19280,19279" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              {cab.advance_enabled ? (
                <>Online advance payment is <b>enabled</b>. Token/advance will be collected via Razorpay and sent to MYF Booking API under <code>payment_details</code> per MYF Manual v1.4.</>
              ) : (
                <>Advance payment is <b>disabled</b>. Bookings are created in direct Pay-to-Driver mode without online payment.</>
              )}
            </div>

            {/* Advance Payment Settings */}
            <div className="pt-4 border-t border-gray-100 space-y-4">
              <h4 className="font-bold text-sm text-[#0B2E5C] flex items-center gap-2">
                <CreditCard size={15} className="text-tp-violet" /> Advance Payment Configuration
              </h4>
              <SwitchRow
                label="Require Online Advance Payment"
                hint="Collect token or full payment online via Razorpay and push payment_details to MYF Booking API. If disabled, bookings default to direct Pay-to-Driver."
                value={cab.advance_enabled}
                onChange={v => update('myf_cabs', 'advance_enabled', v)}
              />
              {cab.advance_enabled && (
                <div className="grid md:grid-cols-2 gap-4 bg-tp-violet-soft/40 p-4 rounded-xl border border-tp-violet/10">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Advance Type</label>
                    <Select value={cab.advance_type || 'fixed'} onValueChange={v => update('myf_cabs', 'advance_type', v)}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="cab-advance-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                        <SelectItem value="percentage">Percentage of Fare (%)</SelectItem>
                        <SelectItem value="full">Full Fare (100% Online)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cab.advance_type !== 'full' && (
                    <Field
                      type="number"
                      label={cab.advance_type === 'percentage' ? "Advance Percentage (%)" : "Advance Amount (₹)"}
                      value={cab.advance_value ?? 100}
                      onChange={v => update('myf_cabs', 'advance_value', Number(v) || 0)}
                      placeholder={cab.advance_type === 'percentage' ? "e.g. 20" : "e.g. 100"}
                    />
                  )}
                </div>
              )}
            </div>

            <Button onClick={testCabs} disabled={testingCab} variant="outline" className="border-tp-violet text-tp-violet" data-testid="cab-test-btn">
              {testingCab ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Test MYF Connection
            </Button>
            {cabTest && (
              <div className={`text-xs rounded-lg p-3 ${cabTest.ok ? 'bg-green-50 text-green-700 border border-green-200' : (cabTest.reachable ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200')}`} data-testid="cab-test-result">
                {cabTest.ok
                  ? `✓ Connection OK — MYF returned fares.${cabTest.request_id ? ' Request ID: ' + cabTest.request_id : ''}`
                  : cabTest.reachable
                    ? `⚠ Reached MYF (auth OK) but: ${cabTest.message}. Check corporate_id or try a covered location.`
                    : `✗ Cannot reach MYF: ${cabTest.message || 'check token/salt/environment'}`}
              </div>
            )}
          </div>
        </TabsContent>

        {/* EMAIL */}
        <TabsContent value="email">
          <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
            <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><Mail size={16} className="text-tp-orange" /> SMTP / Email</h3>
            <SwitchRow label="Enable Email Notifications" hint="Send order confirmations and receipts over email" value={e.enabled} onChange={v => update('email', 'enabled', v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="SMTP Host" value={e.smtp_host} onChange={v => update('email', 'smtp_host', v)} placeholder="smtp.gmail.com" />
              <Field type="number" label="SMTP Port" value={e.smtp_port} onChange={v => update('email', 'smtp_port', Number(v) || 0)} placeholder="587" />
              <Field label="SMTP Username" value={e.smtp_username} onChange={v => update('email', 'smtp_username', v)} placeholder="noreply@..." />
              <Field type="password" label="SMTP Password" value={e.smtp_password} onChange={v => update('email', 'smtp_password', v)} placeholder="••••••" />
              <Field label="From Email" value={e.from_email} onChange={v => update('email', 'from_email', v)} placeholder="no-reply@trainpanda.com" />
              <Field label="From Name" value={e.from_name} onChange={v => update('email', 'from_name', v)} placeholder="TrainPanda" />
            </div>
            <SwitchRow label="Use TLS (recommended)" value={e.smtp_use_tls} onChange={v => update('email', 'smtp_use_tls', v)} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
